import { tv, type VariantProps } from 'tailwind-variants';

import { getHue } from '@/utils/stringHue';

import { cn } from '../lib/utils';

// ── Variants ──────────────────────────────────────────────────────────────────

const avatarVariants = tv({
  base: 'rounded-full ring-2 ring-white shrink-0 select-none inline-flex items-center justify-center overflow-hidden',
  variants: {
    size: {
      xs: 'w-5 h-5 text-[10px]',
      sm: 'w-6 h-6 text-xs',
      md: 'w-8 h-8 text-sm',
      lg: 'w-10 h-10 text-base',
      xl: 'w-12 h-12 text-lg'
    }
  },
  defaultVariants: {
    size: 'md'
  }
});

type AvatarVariants = VariantProps<typeof avatarVariants>;

// ── Helpers ───────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length === 0 || words[0] === '') return '?';
  const first = words[0][0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1][0] ?? '') : '';
  return (first + last).toUpperCase();
}

// ── Component ─────────────────────────────────────────────────────────────────

interface AvatarProps {
  src?: string;
  name?: string;
  size?: AvatarVariants['size'];
  className?: string;
  onClick?: () => void;
}

function Avatar({ src, name = 'User', size = 'md', className, onClick }: AvatarProps) {
  const classes = avatarVariants({ size });

  if (src) {
    return (
      <img
        src={src}
        alt={name}
        onClick={onClick}
        className={cn(classes, 'object-cover', onClick && 'cursor-pointer', className)}
      />
    );
  }

  const initials = getInitials(name);
  const hue = getHue(name);

  return (
    <span
      aria-label={name}
      onClick={onClick}
      className={cn(classes, 'font-semibold text-white', onClick && 'cursor-pointer', className)}
      style={{ backgroundColor: `hsl(${hue}, 55%, 45%)` }}>
      {initials}
    </span>
  );
}

export { Avatar, avatarVariants };
export type { AvatarProps, AvatarVariants };
